import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Alert,
    Card,
    Table,
    Button,
    Input,
    Select,
    Space,
    Tag,
    message,
    Modal,
    Tooltip
} from 'antd';
import {SearchOutlined, ExportOutlined, EyeOutlined, DownloadOutlined, DollarOutlined, WarningOutlined} from '@ant-design/icons';
import {formatCentsAmount} from '../../../utils/invoiceAmount';
import {useQuery, useMutation, useLazyQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {GET_BILL_LISTING, GET_DSP_BILL_STATUS_FILTER_OPTIONS_FOR_INTERNAL, CHECK_DSP_EXPORT_READINESS, CHECK_DSP_BILLS_ADJUST_READINESS} from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {EXPORT_BILL, CREATE_DSP_SUMMARY_EXPORT_TASK, BATCH_CONFIRM_BILLS, EXPORT_DSP_BILL_LIST} from '../../../graphql/mutations/settlement/settlementBillMutations';
import {useConfirmDspBillPayment} from '../../../graphql/mutations/settlement/dspBillPayment';
import {GET_EXPORT_HISTORY} from '../../../graphql/queries/settlement/billExportQueries';
import {BillTimeRangePicker} from '../../../components/settlement/BillTimeRangePicker';
import {ClientSugSelector} from '../../../components/settlement/ClientSugSelector';
import {BillExportModal, type ExportLanguage} from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import DspBillBatchPaymentModal from './components/DspBillBatchPaymentModal';
import ParcelsDownloadModal from './components/ParcelsDownloadModal';
import DspPaymentMethodModal from '../../../components/settlement/DspPaymentMethodModal';
import type {DspPaymentMethod} from '../../../constants/dspPaymentMethod';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {renderBillTypeTag, getBillTypeLabel} from '../../../utils/billTypeConfig';
import {formatDateWithTimezone} from '../../../utils/timezoneFormatter';
import {canExportBill, getExportButtonTitle} from '../../../utils/billExportPermission';
import {getBillStatusColor, getBillStatusLabel} from '../../../utils/billI18n';
import type {
    SettlementBillType,
    BillFiltersInput,
    BillAmountRangeInput,
    PageRequestTypeInput,
    BillExportTemplateType
} from '../../../graphql/types/settlement';
import {BILL_LIST_DEFAULT_PAGE_REQUEST, FrontendBillStatusEnum} from '../../../graphql/types/settlement/CommonTypes';
import { HasResource } from '../../../components/HasResource';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
    consumeSettlementBillListState,
    persistBeforeBillDetailNavigation
} from './settlementBillListState';

const {Option} = Select;

const DSP_DEFAULT_FILTERS: BillFiltersInput = {
    includeCanceled: true,
    includeDeleted: true,
    billType: 'DSP_DELIVERY_FEE_BILL'
};

function getDspListInitialState() {
    const saved = consumeSettlementBillListState('dsp');
    if (saved) {
        return saved;
    }
    return {
        filters: {...DSP_DEFAULT_FILTERS},
        tempFilters: {...DSP_DEFAULT_FILTERS},
        pageRequest: {...BILL_LIST_DEFAULT_PAGE_REQUEST},
        billIdInput: ''
    };
}

function normalizeBillAmountRangeForQuery(
    range: BillAmountRangeInput | undefined
): BillAmountRangeInput | undefined {
    if (!range) return undefined;
    const min = range.minAmount?.trim() ?? '';
    const max = range.maxAmount?.trim() ?? '';
    if (!min && !max) return undefined;
    return {
        minAmount: min || undefined,
        maxAmount: max || undefined,
    };
}

/**
 * DSP账单列表页面
 * 基于 billing_service_frontend_scheduler_plan.md 方案实现
 */
export const SettlementDspBillList: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [initialState] = useState(getDspListInitialState);
    const [filters, setFilters] = useState<BillFiltersInput>(initialState.filters);
    const [tempFilters, setTempFilters] = useState<BillFiltersInput>(initialState.tempFilters);
    const [pageRequest, setPageRequest] = useState<PageRequestTypeInput>(initialState.pageRequest);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [batchPaymentVisible, setBatchPaymentVisible] = useState(false);
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [exportTaskId, setExportTaskId] = useState<string | null>(null);
    const [selectedBill, setSelectedBill] = useState<SettlementBillType | null>(null);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // 正在确认付款的账单ID，用于只让被点击的那一行按钮转圈
    const [confirmingPaymentBillId, setConfirmingPaymentBillId] = useState<string | null>(null);
    // 正在选择付款方式的账单，非空时弹出付款方式弹窗
    const [payingBill, setPayingBill] = useState<SettlementBillType | null>(null);

    // 临时存储账单ID输入值（不立即触发查询）
    const [billIdInput, setBillIdInput] = useState<string>(initialState.billIdInput);

    // GraphQL 查询 - 不使用 skip，让它初始就执行
    const {data, loading, refetch} = useQuery(GET_BILL_LISTING, {
        variables: {filters, pageRequest},
        fetchPolicy: 'cache-and-network' // 先返回缓存（如果有），同时发起网络请求
    });
    const {data: billStatusOptionsData, loading: billStatusOptionsLoading} = useQuery(GET_DSP_BILL_STATUS_FILTER_OPTIONS_FOR_INTERNAL);


    // 监听账单数据更新事件
    useEffect(() => {
        const handleBillDataUpdated = () => {
            refetch();
        };

        window.addEventListener('billDataUpdated', handleBillDataUpdated);

        return () => {
            window.removeEventListener('billDataUpdated', handleBillDataUpdated);
        };
    }, [refetch]);

    // 导出账单变更
    const [exportBill] = useMutation(EXPORT_BILL);

    // 懒加载查询导出历史
    const [checkExportHistory] = useLazyQuery(GET_EXPORT_HISTORY, {
        fetchPolicy: 'network-only'
    });

    // 一键导出前置校验
    const [checkDspExportReadiness, { loading: checkingReadiness }] = useLazyQuery(CHECK_DSP_EXPORT_READINESS, {
        fetchPolicy: 'network-only'
    });

    // 批量内部确认前置校验
    const [checkDspBillsAdjustReadiness, { loading: checkingBillsReadiness }] = useLazyQuery(CHECK_DSP_BILLS_ADJUST_READINESS, {
        fetchPolicy: 'network-only'
    });

    // DSP 结算数据汇总一键导出
    const [createDspSummaryExportTask, { loading: exportDspSummaryLoading }] = useMutation(CREATE_DSP_SUMMARY_EXPORT_TASK);
    const [batchConfirmBills, { loading: batchConfirmLoading }] = useMutation(BATCH_CONFIRM_BILLS);
    const [confirmDspBillPayment] = useConfirmDspBillPayment();
    const [exportDspBillList, { loading: exportBillListLoading }] = useMutation(EXPORT_DSP_BILL_LIST);

    const canOperate = hasResource([ResourceCode.settlement.SETTLEMENT_COST_BILL_OPERATE]);
    const canPay = hasResource([ResourceCode.settlement.SETTLEMENT_DSP_BILL_PAYMENT]);

    const hasExportCondition =
        (tempFilters.clientIds != null && tempFilters.clientIds.length > 0) ||
        (tempFilters.billTimeRange?.startTime != null &&
            tempFilters.billTimeRange?.endTime != null);

    const handleOneClickExport = async () => {
        if (!hasExportCondition) return;
        try {
            const dspIds = tempFilters.clientIds?.length
                ? tempFilters.clientIds.map((id) => Number(id))
                : null;
            const billTimeRange =
                tempFilters.billTimeRange?.startTime && tempFilters.billTimeRange?.endTime
                    ? {
                          startTime: tempFilters.billTimeRange.startTime,
                          endTime: tempFilters.billTimeRange.endTime,
                          timeZoneId: tempFilters.billTimeRange.timeZoneId || 'Asia/Shanghai'
                      }
                    : null;

            const { data: readinessData } = await checkDspExportReadiness({
                variables: { dspIds, billTimeRange }
            });
            const readiness = readinessData?.checkDspExportReadiness;
            if (readiness && !readiness.canExport) {
                message.error(
                    `账单 [${readiness.blockingBillIds.join(', ')}] 有正在处理的明细调账任务，暂时不能进行一键导出`
                );
                return;
            }

            const { data } = await createDspSummaryExportTask({
                variables: { dspIds, billTimeRange }
            });
            const fileName = data?.createDspSummaryExportTask;
            if (!fileName) {
                message.error('提交下载中心失败，请稍后重试');
                return;
            }
            setGeneratedFileName(fileName);
            setDownloadCompleteModalVisible(true);
        } catch (e) {
            message.error('提交下载中心失败: ' + (e as Error).message);
        }
    };

    /**
     * 由筛选框当前内容构建查询条件。查询与导出共用，保证两者口径永远一致。
     * 账单ID 非空时走精确查询，忽略其余筛选项（与原查询行为一致）。
     */
    const buildQueryFilters = useCallback((): BillFiltersInput => {
        if (billIdInput && billIdInput.trim() !== '') {
            return {
                billId: billIdInput,
                includeCanceled: true,
                includeDeleted: true,
                billType: 'DSP_DELIVERY_FEE_BILL'
            };
        }
        const newFilters: BillFiltersInput = {
            ...tempFilters,
            billId: undefined,
            billAmountRange: normalizeBillAmountRangeForQuery(tempFilters.billAmountRange)
        };
        if (!newFilters.billType) {
            newFilters.billType = 'DSP_DELIVERY_FEE_BILL';
        }
        return newFilters;
    }, [billIdInput, tempFilters]);

    /** 应用查询条件到列表并回到第一页 */
    const applyQueryFilters = useCallback((next: BillFiltersInput) => {
        setFilters(next);
        setPageRequest((prev) => ({...prev, page: 1, size: 20}));
    }, []);

    /**
     * 导出账单列表。
     *
     * 导出前先把筛选框条件应用到列表：否则用户改了筛选却没点查询就导出时，
     * 拿到的是上一次查询（或默认全量）的结果，与屏幕所见不符。
     * 同时用同一个条件对象发起导出，保证「导出内容 = 页面所见」这一不变式。
     */
    const handleExportBillList = async () => {
        const queryFilters = buildQueryFilters();
        applyQueryFilters(queryFilters);
        try {
            const {data} = await exportDspBillList({variables: {filters: queryFilters}});
            const fileName = data?.exportDspBillList;
            if (!fileName) {
                message.error('提交下载中心失败，请稍后重试');
                return;
            }
            setGeneratedFileName(fileName);
            setDownloadCompleteModalVisible(true);
        } catch (e) {
            message.error('导出账单列表失败: ' + (e as Error).message);
        }
    };

    // 处理账单类型筛选
    const handleBillTypeChange = (value: string) => {
        setTempFilters(prev => ({
            ...prev,
            billType: value as any
        }));
    };

    // 处理账单状态筛选
    const handleBillStatusChange = (value: string) => {
        setTempFilters(prev => ({
            ...prev,
            billStatus: value as any
        }));
    };

    const handleInvoiceSubmittedChange = (value: boolean | undefined) => {
        setTempFilters(prev => ({
            ...prev,
            invoiceSubmitted: value
        }));
    };

    // 处理时间范围筛选
    const handleTimeRangeChange = (timeRange: any) => {
        setTempFilters(prev => ({
            ...prev,
            billTimeRange: timeRange
        }));

        // 同时保存用户选择的时区，用于显示账单周期
    };

    // 处理分页变化
    const handlePageChange = (page: number, size: number) => {
        setPageRequest((prev) => ({...prev, page, size}));
    };

    // 处理查询操作
    const handleSearch = useCallback(() => {
        applyQueryFilters(buildQueryFilters());
    }, [applyQueryFilters, buildQueryFilters]);

    // 处理查看详情（跳转到 DSP 账单详情页）
    const handleViewDetail = (bill: SettlementBillType) => {
        persistBeforeBillDetailNavigation('dsp', {
            filters,
            tempFilters,
            pageRequest,
            billIdInput
        });
        navigate(`/settlement/dsp-bill-detail/${bill.id}`);
    };

    const handleBatchInternalConfirm = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先勾选要确认的账单');
            return;
        }
        const billIds = selectedRowKeys.map(String); // 账单ID超出JS安全整数，必须以字符串传输
        const { data: adjustReadinessData } = await checkDspBillsAdjustReadiness({
            variables: { billIds }
        });
        const blockingIds: string[] = adjustReadinessData?.checkDspBillsAdjustReadiness ?? [];
        if (blockingIds.length > 0) {
            message.error(`账单 [${blockingIds.join(', ')}] 有正在处理的明细调账任务，暂时不能进行内部确认`);
            return;
        }
        let remark = '';
        Modal.confirm({
            title: '批量内部确认账单',
            content: (
                <div className="pb-6">
                    <div className="mb-2">
                        确定要对已选中的 {selectedRowKeys.length} 张 DSP 运费账单执行内部确认吗？
                    </div>
                    <div className="mb-2">请填写备注：</div>
                    <Input.TextArea
                        maxLength={200}
                        showCount
                        rows={4}
                        placeholder="请输入批量内部确认备注"
                        onChange={(e) => {
                            remark = e.target.value;
                        }}
                    />
                </div>
            ),
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                if (!remark.trim()) {
                    message.warning('请先填写备注');
                    return Promise.reject(new Error('remark required'));
                }
                try {
                    const { data } = await batchConfirmBills({
                        variables: {
                            billIds: selectedRowKeys.map(String),
                            remark: remark.trim()
                        }
                    });
                    const result = data?.batchConfirmBills;
                    if (!result) {
                        message.error('未收到批量确认结果');
                        return Promise.reject(new Error('empty result'));
                    }
                    if (result.successCount > 0) {
                        message.success(`成功确认 ${result.successCount} 张账单`);
                        void refetch();
                        window.dispatchEvent(new Event('billDataUpdated'));
                        setSelectedRowKeys([]);
                    }
                    if (result.failureCount > 0 && result.failures?.length) {
                        Modal.warning({
                            title: `${result.failureCount} 张账单确认失败`,
                            width: 560,
                            content: (
                                <ul className="list-disc pl-5 max-h-64 overflow-y-auto text-left">
                                    {result.failures.map((f: { billId: string; reason: string }) => (
                                        <li key={String(f.billId)}>
                                            账单 {f.billId}：{f.reason}
                                        </li>
                                    ))}
                                </ul>
                            )
                        });
                    }
                } catch (e) {
                    message.error('批量确认失败: ' + (e as Error).message);
                    return Promise.reject(e as Error);
                }
            }
        });
    };

    /**
     * 「确认付款」按钮的展示条件，与详情页 DspBillPaymentInfoCard 完全一致：
     * 需要 DSP 账单付款权限、账单未作废、账期在 DSP Invoice 线上化切换点之后，
     * 且账单正停在「Invoice已确认」——这是 confirmDspBillPayment 唯一接受的前置状态。
     */
    const canConfirmPayment = (bill: SettlementBillType) =>
        canPay &&
        !bill.isCanceled &&
        bill.invoiceEligible === true &&
        bill.status === 'INVOICE_CONFIRMED';

    const handleConfirmPayment = async (paymentMethod: DspPaymentMethod) => {
        if (!payingBill) return;
        setConfirmingPaymentBillId(payingBill.id);
        try {
            const {data} = await confirmDspBillPayment({variables: {billId: payingBill.id, paymentMethod}});
            const result = data?.confirmDspBillPayment;
            if (!result?.success) {
                message.error(result?.errorMessage || '确认付款失败');
                return;
            }
            message.success(t('dspBillPayment.messages.operateSuccess'));
            setPayingBill(null);
            void refetch();
            window.dispatchEvent(new Event('billDataUpdated'));
        } catch (e) {
            message.error('确认付款失败: ' + (e as Error).message);
        } finally {
            setConfirmingPaymentBillId(null);
        }
    };

    // 处理导出账单
    const handleExportBill = (bill: SettlementBillType) => {
        setSelectedBill(bill);
        setExportModalVisible(true);
    };


    // 执行导出（带确认）
    const handleExport = async (template: BillExportTemplateType, language: ExportLanguage) => {
        if (!selectedBill) return;

        try {
            // 先检查是否有导出历史
            message.loading({content: '正在检查导出历史...', key: 'check', duration: 0});
            const {data: historyData} = await checkExportHistory({
                variables: {
                    billId: selectedBill.id,
                    pageRequest: {page: 1, pageSize: 5}
                }
            });
            message.destroy('check');

            const historyRecords = historyData?.getExportHistory?.content || [];
            const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

            // 如果有成功的导出记录，显示确认对话框
            if (successRecords.length > 0) {
                const latestRecord = successRecords[0];
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: '确认导出账单',
                        content: (
                            <div>
                                <p>该账单已有导出记录，最近一次导出：</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>导出时间: {new Date(latestRecord.createdAt).toLocaleString('zh-CN')}</li>
                                    <li>文件名: {latestRecord.fileName}</li>
                                    <li>模板类型: {latestRecord.templateType}</li>
                                </ul>
                                <p style={{marginTop: 12, color: '#faad14'}}>
                                    <strong>提示：</strong>重新导出将生成新的文件，之前的导出记录仍会保留。
                                </p>
                                <p>确定要继续导出吗？</p>
                            </div>
                        ),
                        okText: '确定导出',
                        cancelText: '取消',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                        width: 520
                    });
                });

                if (!confirmResult) {
                    return;
                }
            } else {
                // 没有历史记录，也要确认一下
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: '确认导出账单',
                        content: (
                            <div>
                                <p>将为以下账单创建导出任务：</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>账单ID: {selectedBill.id}</li>
                                    <li>DSP客户: {selectedBill.clientName}</li>
                                    <li>账单类型: {getBillTypeLabel(selectedBill.billType)}</li>
                                    <li>账单金额: {selectedBill.billAmount}</li>
                                </ul>
                                <p style={{marginTop: 12}}>确定要导出吗？</p>
                            </div>
                        ),
                        okText: '确定导出',
                        cancelText: '取消',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });

                if (!confirmResult) {
                    return;
                }
            }

            // 执行导出
            const result = await exportBill({
                variables: {
                    exportRequest: {
                        billId: selectedBill.id,
                        template,
                        format: 'EXCEL',
                        language
                    }
                }
            });

            if (result.data?.exportBill?.success) {
                const taskIds = result.data.exportBill.taskIds || [];
                message.success(`导出任务已创建（${taskIds.length}个文件）`);
                setExportModalVisible(false);
                // 显示进度弹窗（传入所有任务ID）
                setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
                setProgressModalVisible(true);
            } else {
                message.error(result.data?.exportBill?.errorMessage || '导出失败');
            }
        } catch (error) {
            message.destroy('check');
            message.error('导出失败: ' + (error as Error).message);
        }
    };

    // 处理导出完成
    const handleExportCompleted = (task: any) => {
        if (task.status === 'SUCCESS') {
            message.success('导出成功！');
        } else if (task.status === 'FAILED') {
            message.error(`导出失败: ${task.errorMessage || '未知错误'}`);
        }
        refetch(); // 刷新账单列表
    };

    // 格式化日期为 yyyy/mm/dd 格式
    const formatDateOnly = (timeString: string | undefined | null, timeZone: string) => {
        if (!timeString) return '-';
        try {
            // 使用统一的格式化函数，支持 UTC+XX:XX 格式
            // 统一使用横杠格式，与其他时间显示保持一致
            return formatDateWithTimezone(timeString, timeZone, 'zh-CN');
        } catch (error) {
            console.error('Date formatting error:', error);
            return timeString;
        }
    };

    // 表格列定义
    const columns = [
        {
            title: '账单ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
        },
        {
            title: '账单类型',
            dataIndex: 'billType',
            key: 'billType',
            width: 120,
            render: (type: string) => renderBillTypeTag(type),
        },
        {
            title: 'DSP客户',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
        },
        {
            title: 'DSP FULL NAME',
            dataIndex: 'dspFullName',
            key: 'dspFullName',
            width: 220,
            // DSP 主数据缺失或未维护公司全称时下发 null，降级为 '-'
            render: (fullName?: string | null) => fullName || '-',
        },
        {
            title: 'ClientID',
            dataIndex: 'channelId',
            key: 'channelId',
            width: 180,
        },
        {
            title: '结算客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 180,
        },
        {
            title: '账单金额',
            dataIndex: 'billAmount',
            key: 'billAmount',
            width: 150,
            render: (amount: string) => <span style={{whiteSpace: 'nowrap'}}>{amount}</span>,
        },
        {
            title: 'Invoice金额',
            dataIndex: 'invoiceAmount',
            key: 'invoiceAmount',
            width: 170,
            render: (_: unknown, record: SettlementBillType) => {
                if (record.invoiceAmount == null) return <span className="text-gray-400">-</span>;
                const text = formatCentsAmount(record.invoiceAmount, record.invoiceCurrency);
                if (record.invoiceAmountMismatch) {
                    return (
                        <span style={{whiteSpace: 'nowrap'}}>
                            <span className="text-orange-600 mr-1">{text}</span>
                            <Tooltip title="Invoice 金额与账单结算金额不一致">
                                <WarningOutlined className="text-orange-500" />
                            </Tooltip>
                        </span>
                    );
                }
                return <span style={{whiteSpace: 'nowrap'}}>{text}</span>;
            },
        },
        {
            title: '账单状态',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string, record: SettlementBillType) => {
                // 如果账单已作废，显示作废状态
                if (record.isCanceled) {
                    return <Tag color={getBillStatusColor('CANCELED')}>{getBillStatusLabel('CANCELED')}</Tag>;
                }
                return <Tag color={getBillStatusColor(status)}>{getBillStatusLabel(status)}</Tag>;
            },
        },
        {
            title: 'Invoice提交状态',
            dataIndex: 'invoiceSubmitted',
            key: 'invoiceSubmitted',
            width: 120,
            render: (invoiceSubmitted: boolean) => (
                invoiceSubmitted ? <Tag color="green">已提交</Tag> : <Tag>未提交</Tag>
            ),
        },
        {
            title: '账单周期',
            dataIndex: 'periodStart',
            key: 'periodRange',
            width: 280,
            render: (start: string, record: SettlementBillType) => {
                const timeZone = record.timeZone || 'UTC';
                const formattedStart = formatDateOnly(start, timeZone);
                const formattedEnd = formatDateOnly(record.periodEnd, timeZone);
                return (
                    <span style={{whiteSpace: 'nowrap'}}>
            {formattedStart} - {formattedEnd}
          </span>
                );
            },
        },
        {
            title: '时区',
            dataIndex: 'timeZone',
            key: 'timeZone',
            width: 150,
        },
        {
            title: '操作',
            key: 'action',
            width: 300,
            render: (record: SettlementBillType) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined/>}
                        onClick={() => handleViewDetail(record)}
                    >
                        查看详情
                    </Button>
                    {canOperate && (
                        <Button
                            type="link"
                            icon={<ExportOutlined/>}
                            onClick={() => handleExportBill(record)}
                            disabled={!canExportBill(record)}
                            title={getExportButtonTitle(record)}
                        >
                            导出账单
                        </Button>
                    )}
                    {canConfirmPayment(record) && (
                        <Button
                            type="link"
                            icon={<DollarOutlined/>}
                            loading={confirmingPaymentBillId === record.id}
                            onClick={() => setPayingBill(record)}
                        >
                            确认付款
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
                <Card title="账单查询" className="mb-4">
                    {/* 筛选条件 */}
                    <div className="space-y-3">
                        {/* 账单ID */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单ID</label>
                            <Input
                                placeholder="输入账单ID"
                                allowClear
                                value={billIdInput}
                                onChange={(e) => setBillIdInput(e.target.value)}
                                onPressEnter={handleSearch}
                                className="w-64"
                            />
                        </div>

                        {/* DSP客户（多选） */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">DSP客户</label>
                            <ClientSugSelector
                                roleType={1}
                                mode="multiple"
                                placeholder="搜索DSP客户（可多选）..."
                                value={tempFilters.clientIds ?? []}
                                onChange={(val) => {
                                    const ids = Array.isArray(val) ? val : (val ? [val] : []);
                                    setTempFilters(prev => ({
                                        ...prev,
                                        clientIds: ids.length > 0 ? ids : undefined,
                                        clientId: undefined,
                                        clientNameKeyword: ids.length > 0 ? undefined : prev.clientNameKeyword
                                    }));
                                }}
                                className="w-64"
                            />
                        </div>

                        {/* 账单类型（本页仅 billType 2） */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单类型</label>
                            <Select
                                placeholder="选择账单类型"
                                allowClear
                                value={tempFilters.billType}
                                onChange={handleBillTypeChange}
                                className="w-64"
                            >
                                <Option value="DSP_DELIVERY_FEE_BILL">DSP运费账单</Option>
                            </Select>
                        </div>

                        {/* 账单状态 + 包含已作废账单 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">账单状态</label>
                            <Select
                                placeholder="选择账单状态"
                                allowClear
                                value={tempFilters.billStatus}
                                onChange={handleBillStatusChange}
                                className="w-64"
                                loading={billStatusOptionsLoading}
                            >
                                {(billStatusOptionsData?.getDspBillStatusFilterOptionsForInternal || []).map(
                                    (option: {value: FrontendBillStatusEnum; label: string}) => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    )
                                )}
                            </Select>
                            <label className="flex items-center cursor-pointer ml-4">
                                <input
                                    type="checkbox"
                                    checked={tempFilters.includeCanceled}
                                    onChange={(e) => {
                                        setTempFilters(prev => ({
                                            ...prev,
                                            includeCanceled: e.target.checked
                                        }));
                                    }}
                                    className="mr-2 w-4 h-4"
                                />
                                <span className="text-sm text-gray-700">包含已作废账单</span>
                            </label>
                        </div>

                        {/* Invoice 提交状态 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 w-32">Invoice提交状态</label>
                            <Select
                                placeholder="选择Invoice提交状态"
                                allowClear
                                value={tempFilters.invoiceSubmitted}
                                onChange={handleInvoiceSubmittedChange}
                                className="w-64"
                            >
                                <Option value={false}>未提交</Option>
                                <Option value={true}>已提交</Option>
                            </Select>
                        </div>

                        {/* 账单金额范围 */}
                        <div className="flex items-center flex-wrap gap-y-2">
                            <label className="text-sm font-medium text-gray-700 w-32">账单金额</label>
                            <Space wrap>
                                <Input
                                    placeholder="最小金额"
                                    className="w-28"
                                    value={tempFilters.billAmountRange?.minAmount ?? ''}
                                    onChange={(e) =>
                                        setTempFilters(prev => ({
                                            ...prev,
                                            billAmountRange: {
                                                ...prev.billAmountRange,
                                                minAmount: e.target.value
                                            }
                                        }))
                                    }
                                />
                                <span className="text-gray-500">—</span>
                                <Input
                                    placeholder="最大金额"
                                    className="w-28"
                                    value={tempFilters.billAmountRange?.maxAmount ?? ''}
                                    onChange={(e) =>
                                        setTempFilters(prev => ({
                                            ...prev,
                                            billAmountRange: {
                                                ...prev.billAmountRange,
                                                maxAmount: e.target.value
                                            }
                                        }))
                                    }
                                />
                            </Space>
                        </div>

                        {/* 账单时间范围：仅选日期，在所选时区内自动补全天起止时刻 */}
                        <div className="flex items-start gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 shrink-0 pt-1">
                                账单时间范围
                            </label>
                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                <BillTimeRangePicker
                                    pickDateOnly
                                    value={tempFilters.billTimeRange}
                                    onChange={handleTimeRangeChange}
                                    defaultTimeZoneId="America/Los_Angeles"
                                />
                                <Alert
                                    type="info"
                                    showIcon
                                    className="border-blue-200 bg-blue-50 text-gray-900 [&_.ant-alert-message]:font-medium"
                                    message="请选择一个周期的开始时间以及结束的最后一天，注意是该周期的最后一天而不是下一个周期的第一天"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 查询按钮 + 一键导出 */}
                    <div className="flex justify-start items-center gap-3 pt-4 mt-2 border-t border-gray-200">
                        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_BILL_CHECK]}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined/>}
                                onClick={handleSearch}
                                size="large"
                            >
                                查询
                            </Button>
                        </HasResource>
                        {canOperate && (
                            <Tooltip
                                title="请至少选择「DSP客户」或「账单时间范围」其中一个条件后再执行导出。"
                            >
                                <span>
                                    <Button
                                        type="default"
                                        icon={<DownloadOutlined/>}
                                        onClick={handleOneClickExport}
                                        disabled={!hasExportCondition}
                                        loading={exportDspSummaryLoading || checkingReadiness}
                                        size="large"
                                    >
                                        一键导出
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {canOperate && (
                            <Button
                                type="default"
                                icon={<DownloadOutlined/>}
                                onClick={handleExportBillList}
                                loading={exportBillListLoading}
                                size="large"
                            >
                                导出账单列表
                            </Button>
                        )}
                        {canPay && (
                            <Button type="default" onClick={() => setBatchPaymentVisible(true)} size="large">
                                {t('dspBillBatchPayment.upload')}
                            </Button>
                        )}
                    </div>
                </Card>

                <Card
                    title="账单列表"
                    extra={
                        canOperate ? (
                            <Tooltip title={selectedRowKeys.length === 0 ? '请先勾选要确认的账单' : undefined}>
                                <Button
                                    type="primary"
                                    loading={batchConfirmLoading || checkingBillsReadiness}
                                    disabled={selectedRowKeys.length === 0}
                                    onClick={handleBatchInternalConfirm}
                                >
                                    批量内部确认账单
                                </Button>
                            </Tooltip>
                        ) : undefined
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={data?.getBillListing?.content || []}
                        loading={loading}
                        rowKey="id"
                        rowSelection={
                            canOperate
                                ? {
                                      selectedRowKeys,
                                      onChange: (keys) => setSelectedRowKeys(keys),
                                      preserveSelectedRowKeys: true
                                  }
                                : undefined
                        }
                        pagination={false}
                        scroll={{x: 1500}}
                     className="mb-4"/>

                    {data?.getBillListing?.pageInfo && (
                        <TablePaginationBar
                            total={data.getBillListing.pageInfo.totalElements}
                            current={data.getBillListing.pageInfo.page}
                            pageSize={data.getBillListing.pageInfo.size}
                            onChange={handlePageChange}
                        />
                    )}
                </Card>

                {/* 导出模态框 */}
                <BillExportModal
                    visible={exportModalVisible}
                    bill={selectedBill}
                    onCancel={() => setExportModalVisible(false)}
                    onExport={handleExport}
                />

                <DspBillBatchPaymentModal
                    open={batchPaymentVisible}
                    onClose={() => setBatchPaymentVisible(false)}
                    onSuccess={() => {
                        void refetch();
                        window.dispatchEvent(new Event('billDataUpdated'));
                    }}
                />

                {/* 导出进度弹窗 */}
                <BillExportProgressModal
                    taskId={exportTaskId}
                    visible={progressModalVisible}
                    onClose={() => {
                        setProgressModalVisible(false);
                        setExportTaskId(null);
                    }}
                    onCompleted={handleExportCompleted}
                />

                <ParcelsDownloadModal
                    visible={downloadCompleteModalVisible}
                    onClose={() => setDownloadCompleteModalVisible(false)}
                    fileName={generatedFileName}
                />

                <DspPaymentMethodModal
                    open={payingBill !== null}
                    title={t('dspBillPayment.confirm.confirmPayment')}
                    confirmLoading={confirmingPaymentBillId !== null}
                    onOk={handleConfirmPayment}
                    onCancel={() => setPayingBill(null)}
                />
        </div>
    );
};

export default SettlementDspBillList;
